import type { CalendarEvent, CalendarTheme } from './types';
import { COLORS } from './constants';

/**
 * Get background color for an event badge based on status
 */
export const getEventBadgeColor = (
  event: CalendarEvent,
  theme: CalendarTheme
): string => {
  // Check actionPlanData status first if available
  const status = event.actionPlanData?.STATUS?.toLowerCase() || '';
  const assignmentStatus = event.actionPlanData?.ASSIGNMENT_STATUS?.toLowerCase() || '';
  
  // Priority 1: Check if declined
  if (assignmentStatus === 'declined') {
    return '#D40020'; // Red for declined
  }
  
  // Priority 2: Check status
  if (status === 'completed') {
    return '#008553'; // Green for completed
  }
  
  if (status === 'in_progress') {
    return '#316BB8'; // Blue for in progress
  }
  
  if (status === 'overdue') {
    return '#CC6E16'; // Orange for overdue
  }
  
  // Fallback to legacy logic
  if (event.isDone) {
    return theme.eventColors?.done || COLORS.done;
  }
  return event.color || theme.eventColors?.default || COLORS.warning;
};

/**
 * Get text color for an event badge - always white for visibility on colored backgrounds
 */
export const getEventTextColor = (event: CalendarEvent): string => {
  return '#FFFFFF';
};

/**
 * Generate calendar grid for monthly view
 */
export const generateCalendarGrid = (
  firstDay: Date,
  daysInMonth: number,
  startingDayOfWeek: number
): (number | null)[] => {
  const days: (number | null)[] = [];

  // Get previous month info
  const prevMonthYear =
    firstDay.getMonth() === 0 ? firstDay.getFullYear() - 1 : firstDay.getFullYear();
  const prevMonthMonth =
    firstDay.getMonth() === 0 ? 11 : firstDay.getMonth() - 1;
  const prevMonthDaysInMonth = new Date(prevMonthYear, prevMonthMonth + 1, 0).getDate();

  // Add days from previous month
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    days.push(prevMonthDaysInMonth - i);
  }

  // Add days of the current month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  // Add days from next month to fill the grid
  const totalCells = days.length;
  const nextMonthDays = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
  for (let day = 1; day <= nextMonthDays; day++) {
    days.push(1000 + day); // Offset to indicate next month
  }

  return days;
};

/**
 * Get previous month information
 */
export const getPreviousMonthInfo = (firstDay: Date) => {
  const prevMonthYear =
    firstDay.getMonth() === 0 ? firstDay.getFullYear() - 1 : firstDay.getFullYear();
  const prevMonthMonth =
    firstDay.getMonth() === 0 ? 11 : firstDay.getMonth() - 1;

  return { prevMonthYear, prevMonthMonth };
};

/**
 * Determine the display month and year for a day
 */
export const getDisplayMonthYear = (
  day: number,
  isPrevMonth: boolean,
  currentDate: Date
): { displayMonth: number; displayYear: number } => {
  let displayMonth = currentDate.getMonth();
  let displayYear = currentDate.getFullYear();

  if (isPrevMonth) {
    const prevMonthMonth =
      currentDate.getMonth() === 0 ? 11 : currentDate.getMonth() - 1;
    displayMonth = prevMonthMonth;
    displayYear =
      currentDate.getMonth() === 0 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
  } else if (day >= 1000) {
    // Next month
    displayMonth = displayMonth === 11 ? 0 : displayMonth + 1;
    displayYear = displayMonth === 11 ? displayYear + 1 : displayYear;
  }

  return { displayMonth, displayYear };
};
